/**
 * Legal (oct-2026) — aviso de UNA sola vez para usuarios ya registrados (o nueva versión publicada):
 * GET /api/legal/acceptance → si `needs_acceptance`, bloquea la app con un panel hasta que acepten (POST /legal/accept).
 * No se muestra en /legal/* (para que puedan leer los documentos) ni sin sesión.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { LEGAL_PATHS } from '@/components/Legal/LegalLinks';
import { tr } from '@/contexts/LanguageContext';

export const LegalAcceptanceGate = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [state, setState] = useState(null); // null | {needs_acceptance, current}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) { setState(null); return undefined; }
    let alive = true;
    api.get('/legal/acceptance').then((d) => { if (alive) setState(d); }).catch(() => {});
    return () => { alive = false; };
  }, [user?.id]);

  if (!user || !state?.needs_acceptance || pathname.startsWith('/legal/')) return null;

  const accept = async () => {
    setSaving(true);
    try {
      await api.post('/legal/accept', {});
      setState({ ...state, needs_acceptance: false });
      toast.success(tr("Gracias. Guardamos tu aceptación."));
    } catch {
      toast.error(tr("No pudimos guardar tu aceptación. Inténtalo de nuevo."));
    } finally {
      setSaving(false);
    }
  };

  const isNew = Boolean(state.version);
  return (
    <div className="fixed inset-0 z-[120] bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="legal-acceptance-gate">
      <div className="w-full max-w-md rounded-3xl border border-primary/30 bg-card p-6 space-y-4 shadow-2xl">
        <p className="text-[10px] uppercase tracking-[0.25em] text-primary font-bold flex items-center gap-1.5"><Scale className="w-3.5 h-3.5" /> Ziyup</p>
        <h2 className="font-display text-xl font-bold text-foreground">{isNew ? tr("Actualizamos nuestros Términos") : tr("Términos y Privacidad")}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">{tr("Para seguir usando Ziyup necesitamos que aceptes los")}{' '}
          <a href={LEGAL_PATHS.terms} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2" data-testid="legal-gate-terms-link">{tr("Términos de Servicio")}</a>{' '}{tr("y la")}{' '}
          <a href={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2" data-testid="legal-gate-privacy-link">{tr("Política de Privacidad")}</a>
          {' '}{tr("(versión {0}). Guardamos la versión y la fecha de tu aceptación.", state.current?.terms_version)}</p>
        <button
          type="button"
          onClick={accept}
          disabled={saving}
          className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
          data-testid="legal-gate-accept-btn"
        >
          {saving ? tr("Guardando…") : tr("He leído y acepto")}
        </button>
      </div>
    </div>
  );
};

export default LegalAcceptanceGate;
